import { Injectable, signal } from '@angular/core';
import { Router } from '@angular/router';
import { GAME_SCREEN_LAYOUT, GAME_TYPE, IGrigGameSession } from '@libs/store/game/model';
import { MODAL_TYPE } from '@libs/store/toast/model';
import { ICatGamesOffset, IGame, IGameSession, IHttpResponse, IWallet } from '@libs/interfaces';
import { combineLatest, distinctUntilChanged, filter, first, map, Observable, of, skip, switchMap, tap } from 'rxjs';
import { MapResponseData } from '@libs/rxjs/operators.rxjs';
import { pick } from 'lodash';
import { ApiService } from '../api.service';
import { LangService } from '../lang.service';
import { DeviceService } from '../device.service';
import { ParseGameNameFotUrl } from '@libs/helper/casino/games.helper';
import { GetTimestamp00Hours } from '@libs/helper/dates.helper';
import { WalletService } from '../wallet/wallet.service';
import { CasinoStoreService } from '../store/casino-store.service';
import { StoreWalletService } from '../store/store-wallet.service';
import { StoreToastModalsService } from '../store/store-toast-modals.service';

@Injectable({
  providedIn: 'root',
})
export class GamesService {
  LIMIT = 24;
  loadingGame = signal(false);
  constructor(
    private httpsService: ApiService,
    private lang: LangService,
    private router: Router,
    private config: DeviceService,
    private casinoStoreService: CasinoStoreService,
    private walletService: WalletService,
    private storeWalletService: StoreWalletService,
    private storeToastModalsService: StoreToastModalsService,
    private deviceService: DeviceService,
  ) {
    this.storeWalletService
      .selectWallet()
      .pipe(
        filter(Boolean),
        tap((wallet: IWallet) => this.init(wallet)),
        switchMap(() => this.storeGrid()),
        first(),
        tap(() => this.listenChangeWallet()),
      )
      .subscribe();
  }

  /**
   * Initializes the game service with the given wallet.
   * @param {IWallet} wallet - The wallet to initialize with.
   */
  init(wallet: IWallet) {
    const gridGames: any = this.getGridGamesFromLocalStorage();
    const layout: GAME_SCREEN_LAYOUT = this.getStoreGameScreenLayout;
    if (layout) this.casinoStoreService.setGameScreenLayout(layout);
    if (gridGames.length > 0) {
      gridGames.forEach((gridGameSession: IGrigGameSession, index: number) => {
        if (gridGameSession) {
          const wallet_cond = gridGameSession.session.wallet === wallet.code;
          const timestamp_cond = gridGameSession.session.createdAt == GetTimestamp00Hours(Date.now());
          wallet_cond && timestamp_cond && this.casinoStoreService.addGridGameSession(index, gridGameSession);
        }
      });
    }
  }

  /**
   * Listens for changes in the wallet and resets grid games when a change occurs.
   */
  listenChangeWallet() {
    this.storeWalletService
      .selectWallet()
      .pipe(
        skip(1),
        distinctUntilChanged(),
        tap(() => this.resetGridGames()),
      )
      .subscribe();
  }

  /**
   * Toggles the like status for a user game.
   * @param {Partial<IGame>} game - The game to toggle like for.
   * @returns {Observable<any>} An observable of the response data.
   */
  toggleLikeUserGame(game: Partial<IGame>) {
    return this.httpsService.put(`games/game/user/toggle/like/${game.external_id}`, {}).pipe(MapResponseData);
  }

  /**
   * Gets the like status of a game for the current user.
   * @param {Partial<IGame>} game - The game to check.
   * @returns {Observable<boolean>} An observable of the like status.
   */
  getGameUserLike(game: Partial<IGame>): Observable<boolean> {
    return this.httpsService.get(`games/game/user/like/${game.external_id}`).pipe(MapResponseData);
  }

  /**
   * Gets the total number of likes for a game.
   * @param {Partial<IGame>} game - The game to get likes for.
   * @returns {Observable<number>} An observable of the number of likes.
   */
  getGameLikes(game: Partial<IGame>): Observable<number> {
    return this.httpsService.get(`games/game/all/likes/${game.external_id}`).pipe(MapResponseData);
  }

  /**
   * Fetches a game by its name and provider.
   * @param {{ name: string, provider: string }} params - The game name and provider.
   * @returns {Observable<IGame>} An observable of the fetched game.
   */
  fetchGameByName({ name, provider }): Observable<IGame> {
    return this.httpsService.get(`games/game/${ParseGameNameFotUrl(name, provider)}`).pipe(MapResponseData);
  }

  /**
   * Fetches the user's favorite games.
   * @returns {Observable<IGame[]>} An observable of the favorite games.
   */
  fetchFavorites(): Observable<IGame[]> {
    return this.httpsService.get('games/favourites').pipe(MapResponseData);
  }

  /**
   * Fetches the user's recently played games.
   * @returns {Observable<IGame[]>} An observable of the recent games.
   */
  fetchRecents(): Observable<IGame[]> {
    return this.httpsService.get('games/recents').pipe(MapResponseData);
  }

  /**
   * Fetches a game by its external ID.
   * @param {string} external_id - The external ID of the game.
   * @returns {Observable<IGame[]>} An observable of the fetched games.
   */
  fetchByExternal_id(external_id: string): Observable<IGame> {
    return this.httpsService.get(`games/game/external_id/${external_id}`);
  }

  /**
   * Adds a game to the stage based on the current game type and selected index.
   * @param {IGame} game - The game to add.
   * @returns {Observable<any>} An observable of the added game session.
   */
  addGameStage(game: IGame) {
    return combineLatest([
      this.casinoStoreService.selectGameType(),
      this.casinoStoreService.selectSelectedIndex(),
    ]).pipe(
      switchMap(([type, index]: [GAME_TYPE, number]) => {
        this.casinoStoreService.removeSession(index);
        this.loadingGame.set(true);
        return type == GAME_TYPE.FUN && index == 0 ? this.addDemoGameSession(game) : this.openGameIndex(game, index);
      }),
      tap(() => this.loadingGame.set(false)),
    );
  }

  /**
   * Opens a game at the specified index.
   * @param {IGame} game - The game to open.
   * @param {number} index - The index to open the game at (default: 0).
   * @returns {Observable<any>} An observable of the opened game session.
   */
  openGameIndex(game: IGame, index = 0) {
    return this.addRealGameSession(game).pipe(
      map((session: IGameSession) => ({
        session: pick(session, ['_id', 'createdAt', 'platform', 'wallet', 'lang', 'aggregator']),
        game: game,
      })),
      tap((gridGameSession: IGrigGameSession) => this.casinoStoreService.addGridGameSession(index, gridGameSession)),
      switchMap(() => this.storeGrid()),
    );
  }

  /**
   * Stores the current grid state.
   * @returns {Observable<any>} An observable of the stored grid.
   */
  private storeGrid() {
    return this.casinoStoreService
      .selectGridGameSessions()
      .pipe(tap((gridGameSessions: IGrigGameSession[]) => this.setGridGamesToLocalStorage(gridGameSessions)));
  }

  /**
   * Adds a real game session.
   * @param {Partial<IGame>} game - The game to add a session for.
   * @returns {Observable<IGameSession>} An observable of the added game session.
   */
  private addRealGameSession(game: Partial<IGame>): Observable<IGameSession> {
    return this.httpsService
      .post(`games/game/session/add`, {
        game: game.external_id,
        platform: this.deviceService.deviceType,
        wallet: this.walletService.code(),
        lang: this.lang.lang(),
      })
      .pipe(map((response: IHttpResponse) => this.analizeresponse(response)));
  }

  /**
   * Analyzes the response from adding a game session.
   * @param {IHttpResponse} response - The response to analyze.
   * @returns {IGameSession | null} The game session if successful, null otherwise.
   */
  private analizeresponse(response: IHttpResponse) {
    const session = response.data?.gameUrl ? response.data : null;
    !session && this.onErrorResponse(response?.data?.message);
    return session;
  }

  /**
   * Handles error responses when adding a game session.
   * @param {string} message - The error message.
   */
  private onErrorResponse(message) {
    let info = 'CASINO.JNA';
    switch (message) {
      case 'This currency is not enabled for your contract':
        info = 'CASINO.CNATG';
        break;
      case 'Game not found':
        info = 'CASINO.GNF';
        break;
    }
    const toast = {
      id: Date.now(),
      info: info,
      type: MODAL_TYPE.ERROR,
      ext: 4000,
    };
    this.storeToastModalsService.addToast(toast);
  }

  /**
   * Adds a demo game session.
   * @param {Partial<IGame>} game - The game to add a demo session for.
   * @returns {Observable<any>} An observable of the added demo session.
   */
  private addDemoGameSession(game: Partial<IGame>) {
    if (game.demo_game_support) {
      return this.httpsService
        .post(`games/game/session/add/demo`, {
          game: game.external_id,
          platform: this.deviceService.deviceType,
          lang: this.lang.lang(),
          aggregator: game.aggregator,
        })
        .pipe(
          map((response: IHttpResponse) => this.analizeresponse(response)),
          tap((session: IGameSession) =>
            session?.gameUrl ? window.open(session.gameUrl, '_blank') : this.gameNotSupportDemo(),
          ),
        );
    } else {
      this.gameNotSupportDemo();
      this.casinoStoreService.toggleGameType();
      return of(null);
    }
  }

  /**
   * Handles the case when a game does not support demo mode.
   */
  private gameNotSupportDemo() {
    const toast = {
      id: Date.now(),
      info: 'CASINO.EJ',
      type: MODAL_TYPE.ERROR,
    };
    this.storeToastModalsService.addToast(toast);
  }

  /**
   * Resets the grid games.
   */
  private resetGridGames() {
    [0, 1, 2, 3].forEach((index: number) => this.casinoStoreService.addGridGameSession(index, null));
    this.setGridGamesToLocalStorage([]);
  }

  /**
   * Gets the grid games from local storage.
   * @returns {IGrigGameSession[]} The grid games from local storage.
   */
  private getGridGamesFromLocalStorage(): IGrigGameSession[] {
    const gridGames = localStorage.getItem('gridGames');
    return !gridGames || gridGames == 'undefined' ? [] : JSON.parse(gridGames);
  }

  /**
   * Sets the grid games to local storage.
   * @param {IGrigGameSession[]} gridGames - The grid games to store.
   */
  private setGridGamesToLocalStorage(gridGames: IGrigGameSession[]): void {
    gridGames && localStorage.setItem('gridGames', JSON.stringify(gridGames));
  }

  /**
   * Gets the stored game screen layout.
   * @returns {GAME_SCREEN_LAYOUT} The stored game screen layout.
   */
  get getStoreGameScreenLayout(): GAME_SCREEN_LAYOUT {
    if (!localStorage.getItem('GAME_SCREEN_LAYOUT')) {
      this.casinoStoreService.setGameScreenLayout(GAME_SCREEN_LAYOUT.SINGLE);
    }
    return localStorage.getItem('GAME_SCREEN_LAYOUT') as GAME_SCREEN_LAYOUT;
  }

  /**
   * Fetches games for a specific category and provider.
   * @param {string} cat - The category.
   * @param {string} prov - The provider.
   * @returns {Observable<Partial<IGame>[]>} An observable of the fetched games.
   */
  fetchCatProvGames(cat: string, prov: string): Observable<Partial<IGame>[]> {
    return this.httpsService.post(`games/category/provider`, { cat, prov, deviceType: this.deviceType() }).pipe(
      filter((games: Partial<IGame>[]) => games.length > 0),
      tap((games: Partial<IGame>[]) => this.casinoStoreService.addGamesToCatProv(cat, prov, games as IGame[])),
    );
  }

  /**
   * Gets the image URL for a game.
   * @param {Partial<IGame> | IGameActivity} game - The game to get the image URL for.
   * @returns {string} The image URL.
   */

  /**
   * Fetches games for a specific category with pagination.
   * @param {string} cat - The category.
   * @param {number} offset - The pagination offset.
   * @returns {Observable<ICatGamesOffset>} An observable of the fetched games with offset information.
   */
  fetchCategoryGames(cat: string, offset: number): Observable<ICatGamesOffset> {
    return this.httpsService.get(`games/category/${cat}/${this.deviceType()}?offset=${offset}`).pipe(MapResponseData);
  }

  /**
   * Gets the current device type.
   * @returns {string} The device type ('desktop' or 'mobile').
   */
  private deviceType() {
    return this.config.isDesktop ? 'desktop' : 'mobile';
  }

  /**
   * Selects the game activity from the store.
   * @returns {Observable<any>} An observable of the game activity.
   */
  selectGameActivity() {
    return this.casinoStoreService.selectGameActivity();
  }
  /**
   * Expands the Bicho Casino game.
   * This method creates a partial game object with a specific external ID,
   * adds it to the game stage, and navigates to the casino stage.
   * @returns {Observable<any>} An observable that completes after navigation.
   */
  expandBichocasino() {
    const game: Partial<IGame> = {
      external_id: '6654740a1a48666c9d703bc3',
    };
    this.expandGame(game as IGame);
  }
  expandGame(game: IGame) {
    this.addGameStage(game).pipe(first()).subscribe();
    this.casinoStoreService.closeSearcher();
    setTimeout(() => this.router.navigateByUrl('/casino/stage'), 300);
  }
}
